import { useEffect, useMemo, useRef, useState } from "react";
import { Button, SelectOption, Tab, Title } from "rizzui";
import { FormProvider, useForm } from "react-hook-form";
import TelegramConfigurationForm from "./features/TelegramConfigurationFormProps";
import cn from "@/utils/class-names";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  advanceFeatures,
  AdvanceFeaturesSchema,
} from "@/utils/validators/advance-features.schema";
import { IResponseData, ListResponseData } from "@/utilities/types/requests";
import useApi from "@/hooks/useApi";
import AdvanceFeatureRepository from "@/utilities/repositories/AdvanceFeature";
import {
  EFeatureMode,
  EFeatureType,
  IFeature,
  IUserFeature,
} from "@/utilities/types/AdvanceFeature";
import { useParams, useNavigate } from "react-router-dom";
import { findFeatureByType } from "./ultil";
import toast from "react-hot-toast";
import { useAdvanceFeatures } from "@/hooks/use-advance-features";
import LandingPage from "./features/LandingPage";
import MultipleContactPage from "./features/MultipleContact";
import TeamAccountPage from "./features/TeamAccount";
import DirectoryPage from "./features/DirectoryPage";
import AdCredit from "./features/ad-credit/AdCredit";
import { routes } from "@/config/routes";
import GoogleAdsAccountPage from "./features/GoogleAdsAccount";
import SettingsRepository from "@/utilities/repositories/Settings";
import { TGoogleAdsAccount } from "@/utilities/types/Setting";
import UserRepository from "@/utilities/repositories/Users";
import { IUserData } from "@/utilities/types/Users";

const GOOGLE_ADS_ACCOUNT_TAB_ID = "google-ads-account";

export default function AdminSetAdvancedFeaturesPage() {
  const [tabIndex, setTabIndex] = useState(0);
  const googleAdsAccountIdRef = useRef("");
  const { setEnabledLayout } = useAdvanceFeatures();
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(advanceFeatures),
    defaultValues: {
      telegramBot: {
        chatId: "",
        enable: false,
        type: EFeatureType.IpAccessTrackerTelegram,
      },
      dynamicLayout: {
        enable: false,
        dynamicLayoutMode: EFeatureMode.Normal,
        type: EFeatureType.DynamicLayout,
      },
      landingPage: {
        enable: false,
        type: EFeatureType.LandingPage,
        useProjectSgTrackingId: false,
        limitCreation: 0,
      },
      multipleContact: {
        enable: false,
        type: EFeatureType.MultipleContact,
      },
      teamAccount: {
        enable: false,
        type: EFeatureType.TeamAccount,
      },

      directory: {
        enable: false,
        type: EFeatureType.Directory,
      },
      autoAddNewProject: {
        enable: true,
        type: EFeatureType.AutoAddNewProject,
      },
      otpVerification: {
        enable: false,
        type: EFeatureType.OtpVerification,
      },
      googleAdsAccount: {
        googleAdsAccountId: "",
      },
    },
  });

  const { id } = useParams();
  const listModule = [
    EFeatureType.IpAccessTrackerTelegram,
    EFeatureType.MultipleContact,
    EFeatureType.TeamAccount,
    EFeatureType.LandingPage,
    EFeatureType.Directory,
    EFeatureType.AdCredit,
  ];

  const { request: getFeatureByUserId, response } = useApi<
    IResponseData<IUserFeature[]>
  >({
    request: AdvanceFeatureRepository.getUserFeaturesById,
  });

  const { request: getAllFeatureInApp, response: featuresInApp } = useApi<
    ListResponseData<IFeature>
  >({
    request: AdvanceFeatureRepository.getFeaturesInApp,
  });

  const { request: createOrUpdateUserFeature } = useApi({
    request: AdvanceFeatureRepository.createOrUpdateUserFeature,
    enableToast: false,
  });
  const { request: getGoogleAdsAccounts, response: googleAdsAccountsResponse } =
    useApi<IResponseData<TGoogleAdsAccount[]>>({
      request: SettingsRepository.getGoogleAdsAccounts,
    });
  const { request: getUserById, response: userResponse } = useApi<
    IResponseData<IUserData>
  >({
    request: UserRepository.getUserById,
  });

  const modules = (featuresInApp?.data?.items || [])?.filter((module) =>
    listModule.includes(module.type)
  );

  const adCredit: IFeature = {
    id: "ad-credit",
    name: "Ad Credit",
    enabled: true,
    type: EFeatureType.AdCredit,
    createdAt: "",
    updatedAt: "",
    deletedAt: null,
  }; //hard code for all user

  modules.unshift(adCredit);

  const tabs = useMemo(
    () => [
      ...modules.map((module) => ({
        id: module.id,
        name: module.name,
        featureType: module.type,
      })),
      {
        id: GOOGLE_ADS_ACCOUNT_TAB_ID,
        name: "Google Ads Account",
        featureType: null,
      },
    ],
    [modules],
  );

  const googleAdsAccountOptions = useMemo<SelectOption[]>(() => {
    const items = googleAdsAccountsResponse?.data ?? [];
    return items.map((account) => ({
      value: account.id,
      label: `${account.name} (${account.accountId})`,
    }));
  }, [googleAdsAccountsResponse?.data]);

  const onSubmit = async (values: AdvanceFeaturesSchema) => {
    const {
      telegramBot,
      dynamicLayout,
      landingPage,
      multipleContact,
      teamAccount,
      directory,
      autoAddNewProject,
      otpVerification,
      googleAdsAccount,
    } = values;

    const currentTab = tabs[tabIndex];
    if (!currentTab) return;

    if (currentTab.id === GOOGLE_ADS_ACCOUNT_TAB_ID) {
      if (!id) return;
      try {
        await UserRepository.updateUser(id, {
          googleAdsAccountId: googleAdsAccount?.googleAdsAccountId || null,
        });
        toast.success("Success");
      } catch (error: any) {
        toast.error(error?.message || "Failed to update Google Ads account");
      }
      return;
    }

    const submittedFeature = modules?.find(
      (module) => module.type === currentTab.featureType,
    );

    if (!submittedFeature) return;

    const toastId = toast.loading("Loading...");
    switch (submittedFeature.type) {
      case EFeatureType.IpAccessTrackerTelegram: {
        const featureIpAccessTrackerTelegram = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.IpAccessTrackerTelegram
        );
        if (id && featureIpAccessTrackerTelegram?.id) {
          await createOrUpdateUserFeature({
            userId: id,
            chatId: telegramBot?.chatId,
            enabled: telegramBot?.enable,
            featureId: featureIpAccessTrackerTelegram?.id,
          });
          toast.success("Success", { id: toastId });
        }

        break;
      }
      case EFeatureType.DynamicLayout: {
        const featureDynamicLayout = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.DynamicLayout
        );
        if (id && featureDynamicLayout?.id) {
          try {
            await createOrUpdateUserFeature({
              userId: id,
              enabled: dynamicLayout?.enable || false,
              featureId: featureDynamicLayout?.id,
            });
            setEnabledLayout(dynamicLayout?.enable || false);
            toast.success("Success", { id: toastId });
          } catch (error: any) {
            toast.error(error?.msg || error?.error || "Update failed", {
              id: toastId,
            });
          }
        }
        break;
      }
      case EFeatureType.LandingPage: {
        const featureLandingPage = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.LandingPage
        );
        if (id && featureLandingPage?.id) {
          try {
            await createOrUpdateUserFeature({
              userId: id,
              useProjectSgTrackingId:
                landingPage?.useProjectSgTrackingId || false,
              limitCreation: landingPage?.limitCreation
                ? +landingPage.limitCreation
                : null,
              enabled: landingPage?.enable || false,
              featureId: featureLandingPage?.id,
            });
            setEnabledLayout(landingPage?.enable || false);
            toast.success("Success", { id: toastId });
          } catch (error: any) {
            toast.error(error?.msg || error?.error || "Update failed", {
              id: toastId,
            });
          }
        }
        break;
      }

      case EFeatureType.MultipleContact: {
        const featureMultipleContact = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.MultipleContact
        );
        if (id && featureMultipleContact?.id) {
          try {
            await createOrUpdateUserFeature({
              userId: id,
              enabled: multipleContact?.enable || false,
              featureId: featureMultipleContact?.id,
            });
            setEnabledLayout(multipleContact?.enable || false);
            toast.success("Success", { id: toastId });
          } catch (error: any) {
            toast.error(error?.msg || error?.error || "Update failed", {
              id: toastId,
            });
          }
        }
        break;
      }

      case EFeatureType.TeamAccount: {
        const featureTeamAccountPage = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.TeamAccount
        );
        if (id && featureTeamAccountPage?.id) {
          try {
            await createOrUpdateUserFeature({
              userId: id,
              enabled: teamAccount?.enable || false,
              featureId: featureTeamAccountPage?.id,
            });
            setEnabledLayout(teamAccount?.enable || false);
            toast.success("Success", { id: toastId });
          } catch (error: any) {
            toast.error(error?.msg || error?.error || "Update failed", {
              id: toastId,
            });
          }
        }
        break;
      }

      case EFeatureType.Directory: {
        const featureDirectory = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.Directory
        );
        const featureAutoAddNewProject = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.AutoAddNewProject
        );
        const featureDynamicLayout = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.DynamicLayout
        );
        const featureOtpVerification = findFeatureByType(
          featuresInApp?.data?.items as IFeature[],
          EFeatureType.OtpVerification
        );
        try {
          if (!id) break;

          setEnabledLayout(
            directory?.enable || autoAddNewProject?.enable || false
          );
          if (featureDirectory?.id) {
            await createOrUpdateUserFeature({
              userId: id,
              enabled: directory?.enable || false,
              featureId: featureDirectory?.id,
            });
          }
          if (featureAutoAddNewProject?.id) {
            await createOrUpdateUserFeature({
              userId: id,
              enabled: autoAddNewProject?.enable || false,
              featureId: featureAutoAddNewProject?.id,
            });
          }
            if (id && featureDynamicLayout?.id) {
              try {
                await createOrUpdateUserFeature({
                  userId: id,
                  enabled: dynamicLayout?.enable || false,
                  featureId: featureDynamicLayout?.id,
                });
                setEnabledLayout(dynamicLayout?.enable || false);
                toast.success("Success", { id: toastId });
              } catch (error: any) {
                toast.error(error?.msg || error?.error || "Update failed", {
                  id: toastId,
                });
              }
            }
            if (id && featureOtpVerification?.id) {
              try {
                await createOrUpdateUserFeature({
                  userId: id,
                  enabled: otpVerification?.enable || false,
                  featureId: featureOtpVerification?.id,
                });
                toast.success("Success", { id: toastId });
              } catch (error: any) {
                toast.error(error?.msg || error?.error || "Update failed", {
                  id: toastId,
                });
              }
            }
          toast.success("Success", { id: toastId });
        } catch (error: unknown) {
          toast.error(error?.msg || error?.error || "Update failed", {
            id: toastId,
          });
        }

        break;
      }

      default: {
        console.error("Feature not found");
        break;
      }
    }
  };

  const renderTabPanelByFeatureType = (featureType: EFeatureType) => {
    switch (featureType) {
      case EFeatureType.AdCredit:
        return <AdCredit />;
      case EFeatureType.IpAccessTrackerTelegram:
        return <TelegramConfigurationForm name="telegramBot" isSuperAdmin />;
      case EFeatureType.LandingPage:
        return <LandingPage name="landingPage" isSuperAdmin />;
      case EFeatureType.MultipleContact:
        return <MultipleContactPage name="multipleContact" isSuperAdmin />;
      case EFeatureType.TeamAccount:
        return <TeamAccountPage name="teamAccount" isSuperAdmin />;
      case EFeatureType.Directory:
        return (
          <DirectoryPage
            moduleTopName="directory"
            moduleBottomName="autoAddNewProject"
            moduleDynamicLayoutName="dynamicLayout"
            moduleOtpVerificationName="otpVerification"
            isSuperAdmin
          />
        );

      default:
        return <></>;
    }
  };

  // Fetch data when id changes
  useEffect(() => {
    (async () => {
      if (id) {
        await getFeatureByUserId(id);
        await getUserById(id);
      }
      await getGoogleAdsAccounts();
      await getAllFeatureInApp({
        limit: 50,
        page: 1,
      });
    })();
  }, [getAllFeatureInApp, getFeatureByUserId, getGoogleAdsAccounts, getUserById, id]);

  useEffect(() => {
    googleAdsAccountIdRef.current = userResponse?.data?.googleAdsAccountId || "";
  }, [userResponse?.data?.googleAdsAccountId]);

  useEffect(() => {
    if (response?.data) {
      const userFeature = response.data.find(
        (userFeature) =>
          userFeature.feature.type === EFeatureType.IpAccessTrackerTelegram
      );
      const userFeatureDynamicLayout = response.data.find(
        (userFeature) => userFeature.feature.type === EFeatureType.DynamicLayout
      );
      const userFeatureLandingPage = response.data.find(
        (userFeature) => userFeature.feature.type === EFeatureType.LandingPage
      );
      const userFeatureMultipleContact = response.data.find(
        (userFeature) =>
          userFeature.feature.type === EFeatureType.MultipleContact
      );
      const userFeatureTeamAccount = response.data.find(
        (userFeature) => userFeature.feature.type === EFeatureType.TeamAccount
      );
      const userFeatureDirectory = response.data.find(
        (userFeature) => userFeature.feature.type === EFeatureType.Directory
      );
      const userFeatureAutoAddNewProject = response.data.find(
        (userFeature) =>
          userFeature.feature.type === EFeatureType.AutoAddNewProject
      );
      const userFeatureOtpVerification = response.data.find(
        (userFeature) =>
          userFeature.feature.type === EFeatureType.OtpVerification
      );

      form.reset({
        telegramBot: {
          chatId: userFeature?.chatId || "",
          enable: userFeature?.enabled || false,
          type: EFeatureType.IpAccessTrackerTelegram,
        },
        dynamicLayout: {
          enable: userFeatureDynamicLayout?.enabled || false,
          dynamicLayoutMode:
            userFeatureDynamicLayout?.dynamicLayoutMode || EFeatureMode.Normal,
          type: EFeatureType.DynamicLayout,
        },
        landingPage: {
          enable: userFeatureLandingPage?.enabled || false,
          type: EFeatureType.LandingPage,
          useProjectSgTrackingId:
            !!userFeatureLandingPage?.useProjectSgTrackingId || false,
          limitCreation: userFeatureLandingPage?.limitCreation
            ? +userFeatureLandingPage.limitCreation
            : undefined,
        },
        multipleContact: {
          enable: userFeatureMultipleContact?.enabled || false,
          type: EFeatureType.MultipleContact,
        },
        teamAccount: {
          enable: userFeatureTeamAccount?.enabled || false,
          type: EFeatureType.TeamAccount,
        },
        directory: {
          enable: userFeatureDirectory?.enabled || false,
          type: EFeatureType.Directory,
        },
        autoAddNewProject: {
          enable: userFeatureAutoAddNewProject?.enabled || false,
          type: EFeatureType.AutoAddNewProject,
        },
        otpVerification: {
          enable: userFeatureOtpVerification?.enabled || false,
          type: EFeatureType.OtpVerification,
        },
        googleAdsAccount: {
          googleAdsAccountId: googleAdsAccountIdRef.current,
        },
      });
    }
  }, [form, response?.data]);

  useEffect(() => {
    form.setValue(
      "googleAdsAccount.googleAdsAccountId",
      userResponse?.data?.googleAdsAccountId || "",
      {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      },
    );
  }, [form, userResponse?.data?.googleAdsAccountId]);

  const shouldHideSubmitButton =
    tabs[tabIndex]?.featureType === EFeatureType.AdCredit;

  return (
    <div className="relative z-50 mb-4 h-full flex flex-wrap items-center justify-between gap-2.5 @container">
      <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
        Advanced Features
      </Title>
      <FormProvider {...form}>
        <form
          className="flex flex-col h-full w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Tab
            className="col-span-full flex-1"
            selectedIndex={tabIndex}
            onChange={(index) => setTabIndex(index)}
          >
            <Tab.List>
              {tabs.map((item) => {
                return <Tab.ListItem key={item.id}>{item.name}</Tab.ListItem>;
              })}
            </Tab.List>

            <Tab.Panels className={"flex-1 h-full"}>
              {tabs.map((item) => {
                return (
                  <Tab.Panel key={item.id} className="flex flex-col gap-6">
                    {item.id === GOOGLE_ADS_ACCOUNT_TAB_ID ? (
                      <GoogleAdsAccountPage
                        name="googleAdsAccount"
                        options={googleAdsAccountOptions}
                      />
                    ) : (
                      renderTabPanelByFeatureType(item.featureType as EFeatureType)
                    )}
                  </Tab.Panel>
                );
              })}
            </Tab.Panels>
          </Tab>

          <div
            className={cn(
              "col-span-full flex items-center justify-end gap-4 bg-white p-2 sticky bottom-0 left-0 right-0 z-[99]"
            )}
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(routes.user.listing)}
              className="w-full @xl:w-auto"
            >
              Back
            </Button>
            {!shouldHideSubmitButton && (
              <Button
                type="submit"
                id={"form-settings"}
                className="w-full @xl:w-auto"
              >
                {form.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
